import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Upload, Trash2, Loader2, Package, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHUNK_SIZE = 5 * 1024 * 1024;
const MAX_BOYUT = 200 * 1024 * 1024;
const SLOT_ICON = { kurulum: Package, guncelleme: Sparkles };
const SLOT_KISA = { kurulum: 'Kurulum', guncelleme: 'Güncelleme' };

const formatBoyut = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const formatTarih = (iso) => new Date(iso).toLocaleDateString('tr-TR');

export const AppDosyalari = ({ isAdmin = false, dark = false }) => {
  const [dosyalar, setDosyalar] = useState([]);
  const [yukleSlot, setYukleSlot] = useState(null);
  const [lucaDialog, setLucaDialog] = useState(null);

  const yenile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/app-dosyalari`);
      setDosyalar(data);
    } catch (e) {
      console.error('Uygulama dosyaları alınamadı', e);
    }
  }, []);

  useEffect(() => { yenile(); }, [yenile]);

  const adminIndir = async (d) => {
    try {
      const res = await axios.get(`${API}/app-dosyalari/${d.slot}/indir`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }, responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = d.dosya_adi; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('İndirme başarısız');
    }
  };

  const btnClass = dark
    ? 'border-slate-600 bg-slate-800/60 text-slate-100 hover:bg-slate-700 hover:text-white'
    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50';

  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="app-dosyalari">
      {dosyalar.map((d) => {
        const Icon = SLOT_ICON[d.slot];
        const varMi = Boolean(d.dosya_adi);
        const lucaGerekli = d.slot === 'kurulum' && !isAdmin;
        const dogrudanLink = varMi && !lucaGerekli && !isAdmin;
        const title = varMi
          ? `${d.baslik}\n${d.dosya_adi} • ${formatBoyut(d.boyut)}\n${d.surum_notu ? d.surum_notu + ' • ' : ''}${formatTarih(d.yuklenme_tarihi)}${lucaGerekli ? '\nİndirmek için Luca üye numarası gerekir' : ''}`
          : `${d.baslik} henüz yüklenmedi`;
        const icerik = (
          <>
            <Icon className="w-3.5 h-3.5" />
            <span>{SLOT_KISA[d.slot]}</span>
            {varMi && d.surum_notu && <span className={`hidden lg:inline ${dark ? 'text-emerald-300' : 'text-emerald-600'} font-medium`}>{d.surum_notu}</span>}
            {varMi && <Download className="w-3.5 h-3.5 opacity-70" />}
          </>
        );
        return (
          <div key={d.slot} className="flex items-center">
            <Button
              asChild={dogrudanLink}
              variant="outline"
              size="sm"
              disabled={!varMi}
              title={title}
              onClick={!dogrudanLink && varMi ? () => (isAdmin ? adminIndir(d) : setLucaDialog(d)) : undefined}
              className={`h-8 px-2.5 text-xs gap-1.5 ${btnClass} ${isAdmin ? 'rounded-r-none border-r-0' : ''}`}
              data-testid={`app-dosya-indir-${d.slot}`}
            >
              {dogrudanLink ? (
                <a href={`${API}/app-dosyalari/${d.slot}/indir`} download={d.dosya_adi}>{icerik}</a>
              ) : icerik}
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                size="sm"
                title={`${d.baslik} yükle / değiştir`}
                onClick={() => setYukleSlot(d)}
                className={`h-8 px-2 rounded-l-none ${btnClass} text-emerald-600 hover:text-emerald-700`}
                data-testid={`app-dosya-yukle-${d.slot}`}
              >
                <Upload className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        );
      })}
      {yukleSlot && (
        <AppDosyaYukleDialog slot={yukleSlot} onClose={() => setYukleSlot(null)} onDone={() => { setYukleSlot(null); yenile(); }} />
      )}
      {lucaDialog && <LucaUyeDialog dosya={lucaDialog} onClose={() => setLucaDialog(null)} />}
    </div>
  );
};

const LucaUyeDialog = ({ dosya, onClose }) => {
  const [form, setForm] = useState({ ad_soyad: '', luca_uye_no: '', email: '', telefon: '' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const eksik = Object.values(form).some((v) => !v.trim());

  const indir = async (e) => {
    e.preventDefault();
    if (eksik) return toast.error('Lütfen tüm alanları doldurun');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/app-dosyalari/kurulum/indirme-kaydi`, form);
      const a = document.createElement('a');
      a.href = `${API}/app-dosyalari/kurulum/indir?t=${encodeURIComponent(data.indirme_token)}`;
      a.download = dosya.dosya_adi;
      a.click();
      toast.success('İndirme başlatıldı');
      onClose();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : Array.isArray(d) ? d.map((x) => x.msg).join(' ') : 'İndirme başlatılamadı');
    } finally {
      setLoading(false);
    }
  };

  const alan = (k, label, props = {}) => (
    <div>
      <Label htmlFor={`luca-${k}`}>{label} *</Label>
      <Input id={`luca-${k}`} value={form[k]} onChange={set(k)} required data-testid={`luca-${k}-input`} {...props} />
    </div>
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="luca-uye-dialog">
        <DialogHeader>
          <DialogTitle>Kurulum Dosyasını İndir</DialogTitle>
        </DialogHeader>
        <form onSubmit={indir} className="space-y-3 py-2">
          <p className="text-sm text-slate-600">{dosya.dosya_adi} • {formatBoyut(dosya.boyut)}{dosya.surum_notu ? ` • ${dosya.surum_notu}` : ''}</p>
          {alan('ad_soyad', 'Ad Soyad / Unvan', { autoFocus: true, placeholder: 'Ahmet Yılmaz / Yılmaz Müşavirlik', maxLength: 120 })}
          {alan('luca_uye_no', 'Luca Üye No', { placeholder: 'örn. 123456', maxLength: 30 })}
          {alan('email', 'E-posta', { type: 'email', placeholder: 'ornek@email.com' })}
          {alan('telefon', 'Telefon', { type: 'tel', placeholder: '05xx xxx xx xx', maxLength: 20 })}
          <p className="text-xs text-slate-500">Kurulum dosyasını indirmek için tüm bilgiler zorunludur.</p>
          <DialogFooter>
            <Button type="submit" disabled={loading || eksik} className="bg-emerald-600 hover:bg-emerald-700 w-full" data-testid="luca-indir-btn">
              {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
              İndir
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AppDosyaYukleDialog = ({ slot, onClose, onDone }) => {
  const [file, setFile] = useState(null);
  const [surumNotu, setSurumNotu] = useState('');
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(null);
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const yukle = async () => {
    if (!file) return toast.error('Lütfen bir dosya seçin');
    if (file.size > MAX_BOYUT) return toast.error('Dosya boyutu 200 MB\'ı aşamaz');
    setLoading(true);
    setProgress(0);
    try {
      const uploadId = `${Date.now()}${Math.random().toString(36).slice(2, 10)}`;
      const total = Math.ceil(file.size / CHUNK_SIZE);
      for (let i = 0; i < total; i++) {
        const fd = new FormData();
        fd.append('upload_id', uploadId);
        fd.append('chunk_index', i);
        fd.append('total_chunks', total);
        fd.append('dosya_adi', file.name);
        fd.append('surum_notu', surumNotu);
        fd.append('content_type', file.type || 'application/octet-stream');
        fd.append('chunk', file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
        await axios.post(`${API}/app-dosyalari/${slot.slot}/chunk`, fd, { headers: authHeaders(), timeout: 15 * 60 * 1000 });
        setProgress(Math.round(((i + 1) / total) * 100));
      }
      toast.success(`${slot.baslik} yüklendi`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Yükleme başarısız');
    } finally {
      setLoading(false);
      setProgress(null);
    }
  };

  const sil = async () => {
    if (!window.confirm(`${slot.baslik} kaldırılsın mı?`)) return;
    setLoading(true);
    try {
      await axios.delete(`${API}/app-dosyalari/${slot.slot}`, { headers: authHeaders() });
      toast.success('Dosya kaldırıldı');
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Silme başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="app-dosya-yukle-dialog">
        <DialogHeader>
          <DialogTitle>{slot.baslik}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {slot.dosya_adi && (
            <div className="text-xs text-slate-600 bg-slate-50 border rounded-md p-3" data-testid="app-dosya-mevcut">
              <div className="font-medium text-slate-800">Mevcut: {slot.dosya_adi}</div>
              <div>{formatBoyut(slot.boyut)} • {formatTarih(slot.yuklenme_tarihi)}{slot.surum_notu ? ` • ${slot.surum_notu}` : ''}</div>
            </div>
          )}
          <div>
            <Label htmlFor="app-dosya-input">Dosya (en fazla 200 MB)</Label>
            <Input id="app-dosya-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={loading} data-testid="app-dosya-file-input" />
          </div>
          <div>
            <Label htmlFor="app-dosya-surum">Sürüm / Not</Label>
            <Input id="app-dosya-surum" placeholder="örn. v2.3 – Haziran 2026" value={surumNotu} onChange={(e) => setSurumNotu(e.target.value)} maxLength={200} disabled={loading} data-testid="app-dosya-surum-input" />
          </div>
          {progress !== null && (
            <div data-testid="app-dosya-progress">
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${progress}%` }} /></div>
              <p className="text-xs text-gray-600 mt-1">Yükleniyor… %{progress}{progress === 100 ? ' — depolamaya aktarılıyor' : ''}</p>
            </div>
          )}
        </div>
        <DialogFooter className="flex-row justify-between sm:justify-between gap-2">
          {slot.dosya_adi ? (
            <Button variant="ghost" size="sm" onClick={sil} disabled={loading} className="text-red-600 hover:text-red-700" data-testid="app-dosya-sil-btn">
              <Trash2 className="w-4 h-4 mr-1" /> Kaldır
            </Button>
          ) : <span />}
          <Button onClick={yukle} disabled={loading || !file} className="bg-emerald-600 hover:bg-emerald-700" data-testid="app-dosya-yukle-submit">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}
            {slot.dosya_adi ? 'Değiştir' : 'Yükle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
